import { useEffect, useState } from 'react'
import { costMicroEur } from '@fjalim/shared'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router'
import { auth } from '../api'
import { useAuth } from '../auth/AuthContext'
import { useI18n } from '../i18n'
import { applyTheme, readTheme, type ThemeChoice } from '../lib/theme'
import { Wordmark } from './Wordmark'
import styles from './Shell.module.css'

const THEMES: ThemeChoice[] = ['system', 'light', 'dark']

export function Shell() {
  const { account, logout } = useAuth()
  const { t, locale, setLocale, eur } = useI18n()
  const navigate = useNavigate()
  const location = useLocation()
  const [theme, setTheme] = useState<ThemeChoice>(readTheme)
  const [resent, setResent] = useState(false)
  const wide = location.pathname.startsWith('/transcriptions/')
  const lowBalance = account !== null && account.balance_micro_eur < costMicroEur(60)

  useEffect(() => applyTheme(theme), [theme])

  const cycleTheme = () => setTheme((cur) => THEMES[(THEMES.indexOf(cur) + 1) % THEMES.length]!)

  return (
    <div className={styles.shell}>
      <a className={styles.skip} href="#main">
        {t('nav.skip')}
      </a>
      <header className={`${styles.header} ${wide ? '' : styles.sticky}`}>
        <div className={`${styles.bar} ${wide ? styles.compact : ''}`}>
          <Link to="/" className={styles.brand} aria-label={t('app.name')}>
            <Wordmark />
          </Link>
          <nav className={styles.nav} aria-label={t('nav.main')}>
            <NavLink to="/" end className={({ isActive }) => (isActive ? styles.active : undefined)}>
              {t('nav.transcriptions')}
            </NavLink>
            <NavLink to="/keys" className={({ isActive }) => (isActive ? styles.active : undefined)}>
              {t('nav.keys')}
            </NavLink>
            <NavLink to="/billing" className={({ isActive }) => (isActive ? styles.active : undefined)}>
              {t('nav.billing')}
            </NavLink>
          </nav>
          <div className={styles.money}>
            <Link to="/billing" className={styles.balance} title={t('balance.label')} data-low={lowBalance ? 'true' : undefined}>
              <span className="sr-only">{t('balance.label')}: </span>
              {eur(account?.balance_micro_eur ?? 0)}
            </Link>
            <Link to="/billing" className={`btn btn-small ${lowBalance ? 'btn-primary' : ''}`}>
              {t('list.topup')}
            </Link>
          </div>
          <div className={styles.tools}>
            <div className={styles.langs} role="group" aria-label={t('lang.label')}>
              {(['sq', 'en'] as const).map((code) => (
                <button
                  key={code}
                  type="button"
                  className={styles.lang}
                  aria-pressed={locale === code}
                  lang={code}
                  onClick={() => setLocale(code)}
                >
                  {code.toUpperCase()}
                </button>
              ))}
            </div>
            <button type="button" className={styles.lang} onClick={cycleTheme} aria-label={`${t('theme.label')}: ${t(`theme.${theme}`)}`}>
              {theme === 'dark' ? '☾' : theme === 'light' ? '☀' : '◐'}
            </button>
            <button
              type="button"
              className="btn btn-quiet btn-small"
              onClick={() => void logout().then(() => navigate('/login'))}
            >
              {t('nav.logout')}
            </button>
          </div>
        </div>
      </header>
      {account && !account.email_verified ? (
        <div className={styles.banner} role="status">
          <span>{t('auth.pending.body', { email: account.email, minutes: 15 })}</span>
          <button
            type="button"
            className="btn btn-small"
            disabled={resent}
            onClick={() => void auth.resend().then(() => setResent(true))}
          >
            {resent ? t('auth.pending.sent') : t('auth.pending.resend')}
          </button>
        </div>
      ) : null}
      <main id="main" className={wide ? styles.mainWide : undefined} tabIndex={-1}>
        <Outlet />
      </main>
    </div>
  )
}
